import { useEffect, useMemo, useRef, useState } from "react";
import { lagersOf, reasonLabel } from "@lagerlog/core";
import type { Booking } from "@lagerlog/core";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { useWide } from "../wide.ts";
import { fmt } from "./Book.tsx";
import { Parts } from "./Stock.tsx";
import { SwipeRow, useCoarse } from "./Swipe.tsx";
import { SubNav } from "./SubNav.tsx";
import { WORDS, takeWords } from "../brand.ts";

const KINDS: Record<Booking["kind"], string> = { in: "Eingelagert", out: "Entnommen", move: "Umgelagert", count: "Gezählt", convert: "Umgewandelt", cancel: "Storno" };
const when = (at: number) => new Date(at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const PAGE = 100;

/**
 * The protocol (Z4): who booked what when, newest first, with a cancel for each booking. Nothing is
 * deleted: a cancel is a booking of its own and the cancelled one counts as never booked. By default
 * pairs of booking and cancel are hidden (docs/04-domaenenmodell.md, Storno).
 */
export function Protocol() {
  const { store, version } = useStore();
  // „Verbraucht“ in a household's pantry (B1).
  const KIND = { ...KINDS, out: takeWords(store.config?.kind).taken };
  // A move into the freezer with a new best-before (C1) says so.
  const kindOf = (b: Booking) => (b.kind === "move" && b.lotInfo?.frozen ? "Eingefroren" : KIND[b.kind]);
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  const wide = useWide();
  const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
  const [wine, setWine] = useState(params.get("wein") ?? "");
  const [kind, setKind] = useState("");
  const [person, setPerson] = useState("");
  const [query, setQuery] = useState("");
  const [withCancelled, setWithCancelled] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [asked, setAsked] = useState<string | null>(null);
  const coarse = useCoarse();
  /**
   * With a finger a swipe cancels; the cancel is booked after a few seconds, until then the toast
   * offers to take it back (a cancel of a cancel would not count, docs/04-domaenenmodell.md).
   */
  const [pending, setPending] = useState<Booking | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commit = useRef<(() => void) | null>(null);
  useEffect(() => () => commit.current?.(), []);

  const journal = useMemo(() => [...(store.replica?.journal() ?? [])].reverse(), [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelledBy = useMemo(() => {
    const m = new Map<string, Booking>();
    for (const b of journal) if (b.kind === "cancel") m.set(b.cancels, b);
    return m;
  }, [journal]);

  if (!catalog || !ledger) return null;

  function swipe(b: Booking) {
    commit.current?.();
    const book = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      commit.current = null;
      setPending(null);
      void cancel(b);
    };
    commit.current = book;
    timer.current = setTimeout(book, 5000);
    setPending(b);
  }
  function takeBack() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    commit.current = null;
    setPending(null);
  }
  const scope = new Set(store.config?.lagers ?? []);
  const name = (id: string) => catalog.articles.get(id)?.name ?? id;
  const place = (id: string) => {
    const l = catalog.locations.get(id);
    return l ? `${catalog.lagers.get(l.lager)?.name ?? l.lager} · ${l.name}` : id;
  };
  const who = (b: Booking) => store.personName(b.person) ?? b.device ?? "";
  const people = [...new Set(journal.map(who).filter(Boolean))].sort();
  const q = query.trim().toLowerCase();
  const inventoryChanges = [...catalog.inventories.values()]
    .flatMap((inv) => (inv.changes ?? []).map((c) => ({ ...c, inventory: inv.id, name: inv.name })))
    .sort((a, b) => b.noticedAt - a.noticedAt);

  const rows = journal.filter((b) => {
    if (!lagersOf(b).some((l) => scope.has(l))) return false;
    if (b.kind === "cancel") return withCancelled;
    if (!withCancelled && cancelledBy.has(b.id)) return false;
    if (kind && b.kind !== kind) return false;
    if (person && who(b) !== person) return false;
    const article = "article" in b ? b.article : b.kind === "convert" ? b.fromArticle : "";
    if (wine && article !== wine) return false;
    if (q && !name(article).toLowerCase().includes(q)) return false;
    return true;
  });

  const detail = (b: Booking) => {
    switch (b.kind) {
      case "in":
      case "out":
        return `${reasonLabel(b.reason, catalog.articles.get(b.article)?.fields)} · ${place(b.location)}${b.note?.startsWith("inventory:") ? " · Inventur" : ""}`;
      case "move":
        return `${place(b.from)} → ${place(b.to)}`;
      case "count":
        return `${place(b.location)}${b.inventory ? ` · ${catalog.inventories.get(b.inventory)?.name ?? "Inventur"}` : ""}`;
      case "cancel": {
        const target = journal.find((x) => x.id === b.cancels);
        return target ? `nimmt zurück: ${kindOf(target)} ${when(target.at)}` : "nimmt eine Buchung zurück";
      }
      default:
        return place(b.location);
    }
  };

  async function cancel(b: Booking) {
    if (b.kind === "cancel") return;
    await store.book({ kind: "cancel", cancels: b.id, lagers: lagersOf(b) } as Booking);
    setAsked(null);
  }

  const action = (b: Booking) => {
    if (b.kind === "cancel") return null;
    const by = cancelledBy.get(b.id);
    if (by) return <span className="muted">storniert {when(by.at)}</span>;
    if (pending?.id === b.id) return <span className="muted">wird storniert</span>;
    if (coarse) return null;
    if (asked === b.id)
      return (
        <span className="row">
          <button className="danger" onClick={() => cancel(b)} data-testid="protocol-cancel-confirm">
            Stornieren
          </button>
          <button className="link" onClick={() => setAsked(null)}>
            nein
          </button>
        </span>
      );
    return (
      <button className="link" onClick={() => setAsked(b.id)} data-testid="protocol-cancel">
        Stornieren
      </button>
    );
  };

  const qty = (b: Booking) => {
    if (b.kind === "cancel") return "";
    if (b.kind === "convert") return `${b.fromQty} → ${b.toQty}`;
    return <Parts a={catalog.articles.get(b.article)} qty={b.qty} />;
  };
  const article = (b: Booking) => (b.kind === "cancel" ? "" : name(b.kind === "convert" ? b.fromArticle : b.article));

  return (
    <div className="protocol">
      <SubNav current="protokoll" />
      <h2>Logbuch</h2>
      <div className="filters-row">
        <input placeholder={WORDS.search} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="protocol-search" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Art">
          <option value="">alle Arten</option>
          {(["out", "in", "move", "count"] as const).map((k) => (
            <option key={k} value={k}>
              {KIND[k]}
            </option>
          ))}
        </select>
        <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person">
          <option value="">alle Personen</option>
          {people.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
        <label className="check">
          <input type="checkbox" checked={withCancelled} onChange={(e) => setWithCancelled(e.target.checked)} />
          Stornierte zeigen
        </label>
      </div>
      {inventoryChanges.length > 0 && (
        <section className="banner-box" data-testid="protocol-inventory-changes">
          <strong>Abgeschlossene Inventuren, nachträglich geändert</strong>
          <ul>
            {inventoryChanges.map((c) => (
              <li key={`${c.inventory}|${c.article}|${c.location}|${c.after}`}>
                {when(c.noticedAt)} · {c.name}: {name(c.article)}, {place(c.location)} – {c.before < 0 || c.after < 0 ? "Schwund" : "Differenz"} von {fmt(catalog.articles.get(c.article), Math.abs(c.before))} auf {fmt(catalog.articles.get(c.article), Math.abs(c.after))}
              </li>
            ))}
          </ul>
        </section>
      )}
      {wine && (
        <p className="row between">
          <span>
            Nur <strong>{name(wine)}</strong>
          </span>
          <button className="link" onClick={() => setWine("")}>
            alle {drinks ? "Getränke" : WORDS.many}
          </button>
        </p>
      )}
      <p className="muted">
        {coarse ? "Zum Stornieren nach rechts wischen. " : ""}
        {rows.length} {rows.length === 1 ? "Buchung" : "Buchungen"}
        {ledger.cutoff ? ` seit ${new Date(ledger.cutoff).toLocaleDateString("de-DE")} (was dieses Gerät hält; Älteres im Export)` : ""}
      </p>
      {wide ? (
        <table className="grid">
          <thead>
            <tr>
              <th>Wann</th>
              <th>Wer</th>
              <th>Was</th>
              <th>{drinks ? "Getränk" : WORDS.one}</th>
              <th>Menge</th>
              <th>Grund oder Ort</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((b) => (
              <SwipeRow as="tr" key={b.id} label="Stornieren" off={!coarse || b.kind === "cancel" || cancelledBy.has(b.id) || pending?.id === b.id} onSwipe={() => swipe(b)} className={cancelledBy.has(b.id) || b.kind === "cancel" || pending?.id === b.id ? "gone" : ""} testid="protocol-row">
                <td className="nowrap">{when(b.at)}</td>
                <td>{who(b)}</td>
                <td>{kindOf(b)}</td>
                <td>{article(b)}</td>
                <td className="stack-cell">{qty(b)}</td>
                <td>{detail(b)}</td>
                <td className="num">{action(b)}</td>
              </SwipeRow>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="list">
          {rows.slice(0, shown).map((b) => (
            <SwipeRow key={b.id} label="Stornieren" off={!coarse || b.kind === "cancel" || cancelledBy.has(b.id) || pending?.id === b.id} onSwipe={() => swipe(b)} className={`entry-block ${cancelledBy.has(b.id) || b.kind === "cancel" || pending?.id === b.id ? "gone" : ""}`} testid="protocol-row">
              <div className="row between grow">
                <span>
                  <span className="muted">
                    {when(b.at)} · {who(b)} · {kindOf(b)}
                  </span>
                  <br />
                  {b.kind !== "cancel" && b.kind !== "convert" ? `${fmt(catalog.articles.get(b.article), b.qty)} ` : ""}
                  {article(b)}
                  <br />
                  <span className="muted">{detail(b)}</span>
                </span>
                <span className="entry-action">{action(b)}</span>
              </div>
            </SwipeRow>
          ))}
        </ul>
      )}
      {rows.length > shown && (
        <button className="wide" onClick={() => setShown(shown + PAGE)}>
          Weitere {Math.min(PAGE, rows.length - shown)} zeigen
        </button>
      )}
      {pending && (
        <div className="toast" data-testid="protocol-toast">
          Storniert: {article(pending)}
          <button onClick={takeBack}>Rückgängig</button>
        </div>
      )}
    </div>
  );
}
